/**
 * Форматирование чисел, кристаллов, дат, длительностей и расстояний.
 *
 * Назначение файла: единственный разрешённый способ превратить число в текст.
 * Прямые вызовы toLocaleString в коде запрещены линтером, потому что они игнорируют
 * локаль игрока и системы измерений (docs/19-localization.md §19.8, eslint.config.js).
 *
 * Импорты: типы локалей из ./locales. Внутри — только стандартный Intl,
 * никаких сторонних библиотек: браузер и Node 22 умеют всё нужное сами.
 *
 * Кэширование: экземпляры Intl.* создаются один раз на локаль. Создание форматтера
 * стоит дорого, а карта перерисовывается часто — на списке из сотни карточек сот
 * разница заметна.
 */

import { localeMeta, type LocaleCode } from "./locales.js";

/** Символы валют игры. Не переводятся: это часть визуального языка (§19.3). */
export const CRYSTAL_SYMBOL = {
  /** Кристаллы улья — заработанные. */
  hive: "☾",
  /** Кристаллы кассы — купленные. */
  cash: "✦",
} as const;

export type CrystalKind = keyof typeof CRYSTAL_SYMBOL;

// ─────────────────────────────────────────────────────────────────────────────
// Кэш форматтеров
// ─────────────────────────────────────────────────────────────────────────────

const numberFormatters = new Map<string, Intl.NumberFormat>();
const dateFormatters = new Map<string, Intl.DateTimeFormat>();
const collators = new Map<string, Intl.Collator>();

function numberFormatter(
  locale: LocaleCode,
  options: Intl.NumberFormatOptions = {},
): Intl.NumberFormat {
  const key = `${locale}:${JSON.stringify(options)}`;
  let formatter = numberFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, options);
    numberFormatters.set(key, formatter);
  }
  return formatter;
}

function dateFormatter(
  locale: LocaleCode,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = `${locale}:${JSON.stringify(options)}`;
  let formatter = dateFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, options);
    dateFormatters.set(key, formatter);
  }
  return formatter;
}

// ─────────────────────────────────────────────────────────────────────────────
// Числа и кристаллы
// ─────────────────────────────────────────────────────────────────────────────

/** Целое число с разделителями разрядов по правилам локали: 1 234 / 1,234 / 1.234. */
export function formatNumber(locale: LocaleCode, value: number): string {
  return numberFormatter(locale, { maximumFractionDigits: 0 }).format(value);
}

/** Процент: 0.15 → «15 %». Ручная приписка знака ломается в половине языков. */
export function formatPercent(locale: LocaleCode, ratio: number): string {
  return numberFormatter(locale, { style: "percent", maximumFractionDigits: 0 }).format(ratio);
}

/**
 * Кристаллы для интерфейса: «214 ☾».
 *
 * Вход в milli — во внутренних единицах сервера (1 ☾ = 1000 milli), потому что
 * именно так деньги приходят из API. Округление вниз соответствует правилу Э6.1:
 * игрок никогда не видит больше, чем у него есть.
 */
export function formatCrystals(
  locale: LocaleCode,
  milli: number,
  kind: CrystalKind = "hive",
): string {
  const crystals = Math.floor(milli / 1000);
  return `${formatNumber(locale, crystals)} ${CRYSTAL_SYMBOL[kind]}`;
}

/**
 * Отрицательная сумма для показа долга: «−48 ☾».
 * Знак берётся из локали (в арабском он другой), поэтому число передаётся уже со знаком.
 */
export function formatCrystalsSigned(
  locale: LocaleCode,
  milli: number,
  kind: CrystalKind = "hive",
): string {
  const crystals = Math.trunc(milli / 1000);
  const formatted = numberFormatter(locale, {
    maximumFractionDigits: 0,
    signDisplay: "exceptZero",
  }).format(crystals);
  return `${formatted} ${CRYSTAL_SYMBOL[kind]}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Дата и время
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Дата и время в поясе игрока.
 *
 * Часовой пояс обязателен параметром и не берётся из системы: окно ответа на осаду
 * и экономические сутки считаются по поясу Очага, а игрок может смотреть игру
 * из другого места (docs/economy/01-currencies.md).
 */
export function formatDateTime(locale: LocaleCode, date: Date, timeZone: string): string {
  return dateFormatter(locale, {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone,
  }).format(date);
}

/** Только время: «18:40» или «6:40 PM» — формат выбирает локаль. */
export function formatTime(locale: LocaleCode, date: Date, timeZone: string): string {
  return dateFormatter(locale, { timeStyle: "short", timeZone }).format(date);
}

/**
 * Длительность до события словами: «ещё 6 ч 20 мин».
 *
 * Собирается через Intl.NumberFormat с единицами, а не склейкой строк: в русском
 * «ч» и «мин» пишутся иначе, чем в английском, а в японском порядок частей другой.
 * Показываются максимум две старшие единицы — «6 ч 20 мин 13 с» никому не нужно.
 */
export function formatDuration(locale: LocaleCode, milliseconds: number): string {
  if (milliseconds < 0) throw new Error("Длительность не может быть отрицательной");

  const totalMinutes = Math.floor(milliseconds / 60_000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  const unit = (value: number, unitName: "day" | "hour" | "minute"): string =>
    numberFormatter(locale, { style: "unit", unit: unitName, unitDisplay: "short" }).format(value);

  if (days > 0) return `${unit(days, "day")} ${unit(hours, "hour")}`;
  if (hours > 0) return `${unit(hours, "hour")} ${unit(minutes, "minute")}`;
  return unit(minutes, "minute");
}

// ─────────────────────────────────────────────────────────────────────────────
// Расстояния
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Расстояние до соты или до Очага.
 *
 * Игровые радиусы (66 м у соты, кольцо 1) всегда считаются в метрах; система измерений
 * влияет только на отображение (§19.8). Для imperial порог переключения — 0.1 мили,
 * иначе игрок видел бы «528 футов» вместо «0,1 мили».
 */
export function formatDistance(
  locale: LocaleCode,
  meters: number,
  measurement: "metric" | "imperial" = localeMeta(locale).measurement,
): string {
  if (meters < 0) throw new Error("Расстояние не может быть отрицательным");

  if (measurement === "imperial") {
    const miles = meters / 1609.344;
    if (miles < 0.1) {
      const feet = Math.round(meters * 3.28084);
      return numberFormatter(locale, { style: "unit", unit: "foot", unitDisplay: "short" }).format(
        feet,
      );
    }
    return numberFormatter(locale, {
      style: "unit",
      unit: "mile",
      unitDisplay: "short",
      maximumFractionDigits: 1,
    }).format(miles);
  }

  if (meters < 1000) {
    return numberFormatter(locale, { style: "unit", unit: "meter", unitDisplay: "short" }).format(
      Math.round(meters),
    );
  }
  return numberFormatter(locale, {
    style: "unit",
    unit: "kilometer",
    unitDisplay: "short",
    maximumFractionDigits: 1,
  }).format(meters / 1000);
}

// ─────────────────────────────────────────────────────────────────────────────
// Сортировка
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Сравнение строк по правилам локали: списки клана и рейтинги.
 * Обычное сравнение строк ставит «Ё» после «Я», а умлауты — после всей латиницы,
 * поэтому сортировка без Intl.Collator выглядит сломанной (§19.8).
 */
export function compareStrings(locale: LocaleCode, a: string, b: string): number {
  let collator = collators.get(locale);
  if (!collator) {
    collator = new Intl.Collator(locale, { sensitivity: "base", numeric: true });
    collators.set(locale, collator);
  }
  return collator.compare(a, b);
}

/**
 * Изоляция пользовательского текста внутри фразы (§19.9).
 * Ник латиницей внутри арабского предложения без изолятов «уезжает» в другую часть
 * строки — это не косметика, а нечитаемый текст.
 */
export function isolate(text: string): string {
  return `\u2068${text}\u2069`;
}
